import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
// Avoid importing heavy generated DB types to prevent deep type instantiation
import { useSupabase, queryKeys } from '../provider';

// =====================================================
// WORK ASSIGNMENT QUERIES
// =====================================================

interface AssignmentFilters {
  projectId?: string;
  traineeId?: string;
  stationId?: string;
  date?: string;
  status?: string;
}

export function useWorkAssignments(filters?: AssignmentFilters) {
  const supabase: any = useSupabase();

  return useQuery<any[]>({
    queryKey: queryKeys.production.assignments(filters),
    queryFn: async () => {
      let query = supabase
        .from('work_assignments')
        .select(`
          *,
          project:projects(id, project_number, service_item:service_items(id, service_item_name)),
          trainee:trainees(id, app_user:app_users(id, full_name)),
          station:stations(id, station_name, station_code)
        `)
        .order('assignment_date', { ascending: false });

      // Add null/undefined validation before applying filters
      if (filters?.projectId && filters.projectId.trim() !== '') {
        query = query.eq('project_id', filters.projectId);
      }
      if (filters?.traineeId && filters.traineeId.trim() !== '') {
        query = query.eq('trainee_id', filters.traineeId);
      }
      if (filters?.stationId && filters.stationId.trim() !== '') {
        query = query.eq('station_id', filters.stationId);
      }
      if (filters?.date && filters.date.trim() !== '') {
        query = query.eq('assignment_date', filters.date);
      }
      if (filters?.status && filters.status.trim() !== '') {
        query = query.eq('assignment_status', filters.status);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data;
    },
  });
}

interface ProductionLogFilters {
  assignmentId?: string;
  traineeId?: string;
  projectId?: string;
  limit?: number;
}

export function useProductionLogs(filters?: ProductionLogFilters) {
  const supabase = useSupabase();

  return useQuery<any[]>({
    queryKey: queryKeys.production.logs(JSON.stringify(filters ?? {})),
    queryFn: async () => {
      // If filtering by traineeId or projectId, resolve matching work_assignment ids first
      let assignmentIds: string[] | undefined;

      if (!filters?.assignmentId && (filters?.traineeId || filters?.projectId)) {
        let asgQuery = supabase.from('work_assignments').select('id');
        // Add null validation before applying filters
        if (filters?.traineeId && filters.traineeId.trim() !== '') {
          asgQuery = asgQuery.eq('trainee_id', filters.traineeId);
        }
        if (filters?.projectId && filters.projectId.trim() !== '') {
          asgQuery = asgQuery.eq('project_id', filters.projectId);
        }

        const { data: asgData, error: asgError } = await asgQuery;
        if (asgError) throw asgError;
        assignmentIds = asgData?.map((a: any) => a.id) ?? [];
        // If there are no matching assignments, return empty result early
        if (assignmentIds.length === 0) return [];
      }

      let query = supabase
        .from('production_logs')
        .select('*')
        .order('log_date', { ascending: false }); // Fixed: use log_date instead of logged_at

      if (filters?.assignmentId && filters.assignmentId.trim() !== '') {
        query = query.eq('work_assignment_id', filters.assignmentId);
      }

      // Prevent empty array in .in() filter
      if (assignmentIds && assignmentIds.length > 0) {
        query = query.in('work_assignment_id', assignmentIds);
      }

      if (filters?.limit) {
        query = query.limit(filters.limit);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data;
    },
  });
}

// =====================================================
// WORK ASSIGNMENT MUTATIONS
// =====================================================

type AssignmentInsert = any;
type AssignmentUpdate = any;
type ProductionLogInsert = any;

export function useCreateAssignment() {
  const supabase = useSupabase();
  const queryClient = useQueryClient();

  return useMutation<any, Error, AssignmentInsert>({
    mutationFn: async (assignment: AssignmentInsert) => {
      const { data, error } = await supabase
        .from('work_assignments')
        .insert(assignment as never)
        .select()
        .single();

      if (error) throw error;
      return data as any;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.production.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.detail(data.project_id) });
      toast.success('Work assignment created');
    },
    onError: (error: any) => {
      toast.error(`Failed to create assignment: ${error?.message ?? String(error)}`);
    },
  });
}

export function useUpdateAssignment() {
  const supabase = useSupabase();
  const queryClient = useQueryClient();

  return useMutation<any, Error, AssignmentUpdate & { id: string }>({
    mutationFn: async ({ id, ...updates }: AssignmentUpdate & { id: string }) => {
      const { data, error } = await supabase
        .from('work_assignments')
        .update(updates as never)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as any;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.production.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.detail(data.project_id) });
      toast.success('Assignment updated');
    },
    onError: (error: any) => {
      toast.error(`Failed to update assignment: ${error?.message ?? String(error)}`);
    },
  });
}

export function useLogProduction() {
  const supabase = useSupabase();
  const queryClient = useQueryClient();

  return useMutation<any, Error, ProductionLogInsert>({
    mutationFn: async (log: ProductionLogInsert) => {
      const { data, error } = await supabase
        .from('production_logs')
        .insert(log as never)
        .select()
        .single();

      if (error) throw error;
      return data as any;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.production.logs(data.work_assignment_id),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.production.all });
      toast.success('Production logged');
    },
    onError: (error: any) => {
      toast.error(`Failed to log production: ${error?.message ?? String(error)}`);
    },
  });
}

export function useBulkCreateAssignments() {
  const supabase: any = useSupabase();
  const queryClient = useQueryClient();

  return useMutation<any, Error, AssignmentInsert[]>({
    mutationFn: async (assignments: AssignmentInsert[]) => {
      const { data, error } = await supabase
        .from('work_assignments')
        .insert(assignments as never)
        .select();

      if (error) throw error;
      return data as any;
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.production.all });
      // Invalidate all affected projects
      const d = data as any[];
      const projectIds = [...new Set((d ?? []).map((a: any) => a.project_id))];
      projectIds.forEach((projectId) => {
        queryClient.invalidateQueries({ queryKey: queryKeys.projects.detail(projectId) });
      });
      toast.success(`${d?.length ?? 0} assignments created`);
    },
    onError: (error: any) => {
      toast.error(`Failed to create assignments: ${error?.message ?? String(error)}`);
    },
  });
}
